import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import { iasApi, airportApi } from '../config/api';
import JoumpaAirportSelect from '../components/JoumpaAirportSelect';

const IasIntegrationData = ({ onImportProduct }) => {
  const toast = useToast();
  const [source, setSource] = useState('bss');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  const [joumpaAirport, setJoumpaAirport] = useState('cgk');
  const [joumpaFlightType, setJoumpaFlightType] = useState('departure');
  const [joumpaOrigin, setJoumpaOrigin] = useState('cgk');
  const [joumpaDestination, setJoumpaDestination] = useState('sin');
  const [loungeBranch, setLoungeBranch] = useState('CGK');
  const [airportOptions, setAirportOptions] = useState([]);
  const [airportSearch, setAirportSearch] = useState('');

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        source,
      };
      if (source === 'joumpa') {
        params.airport = joumpaAirport;
        params.flightType = joumpaFlightType;
        params.route = `${joumpaOrigin}-${joumpaDestination}`;
      }
      if (source === 'lounge') {
        params.branch = loungeBranch;
      }

      const response = await iasApi.get('/source-products', { params });
      const rows = response?.data?.data?.products || [];
      setProducts(Array.isArray(rows) ? rows : []);
      setLastSyncAt(new Date());
      toast({
        title: 'Source produk IAS diperbarui',
        description: `${rows.length || 0} produk berhasil dimuat`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'IAS API Error',
        description: err?.response?.data?.message || err.message || 'Gagal memuat source products',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [joumpaAirport, joumpaFlightType, joumpaOrigin, joumpaDestination, loungeBranch, source, toast]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (source === 'lounge') {
      airportApi.get('/list').then((res) => {
        const list = res?.data?.data || [];
        setAirportOptions(Array.isArray(list) ? list : []);
      }).catch(() => {
        setAirportOptions([]);
      });
    }
  }, [source]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((item) => {
      const idText = item?.id != null ? String(item.id).toLowerCase() : '';
      const name = String(item?.name || '').toLowerCase();
      const code = String(item?.code || '').toLowerCase();
      const currency = String(item?.currency || '').toLowerCase();
      const sourceLabel = String(item?.sourceLabel || '').toLowerCase();
      return idText.includes(q) || name.includes(q) || code.includes(q) || currency.includes(q) || sourceLabel.includes(q);
    });
  }, [products, search]);

  const handleImport = async (item) => {
    if (typeof onImportProduct !== 'function') return;
    // Carry the route context the operator selected so it can be persisted on the
    // product (BPPRODUCT_META) and later sent to IAS at dispatch. Without this the
    // Joumpa adapter falls back to CGK→CGK defaults regardless of the real route.
    let routeContext =
      item?.source === 'joumpa'
        ? {
            airport: joumpaAirport,
            flt_type: joumpaFlightType,
            airport_origin: joumpaOrigin,
            airport_destination: joumpaDestination,
          }
        : item?.source === 'lounge'
        ? { branch: loungeBranch }
        : {};

    // Joumpa's product_id/rate_id are route-specific (per IAS) — fetch rate_id now
    // so it's captured at import time instead of defaulting to '1' at dispatch.
    if (item?.source === 'joumpa' && item?.id) {
      try {
        const rateRes = await iasApi.get('/joumpa/rate', {
          params: { airport: joumpaAirport, product_id: item.id, qty: 1 },
        });
        const rateData = rateRes?.data?.data;
        const rateRow = Array.isArray(rateData) ? rateData[0] : rateData;
        const rateId = rateRow?.rate_id ?? rateRow?.RATE_ID ?? null;
        if (rateId != null) {
          routeContext = { ...routeContext, rate_id: rateId };
        } else {
          toast({
            title: 'Rate ID tidak ditemukan',
            description: 'Produk diimport tanpa rate_id — lengkapi manual sebelum publish.',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
        }
      } catch (err) {
        toast({
          title: 'Gagal mengambil rate Joumpa',
          description: err?.response?.data?.message || err.message || 'Produk tetap diimport tanpa rate_id.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      }
    }

    onImportProduct({
      ...item,
      ...routeContext,
      importedAt: Date.now(),
    });
  };

  return (
    <Box>
      <VStack align="stretch" spacing={4} mb={6}>
        <Heading size={{ base: 'md', lg: 'lg' }} color="gray.700">
          Aviation Services - IAS Source Products
        </Heading>
        <Text fontSize="sm" color="gray.500">
          Gunakan daftar ini sebagai source import produk IAS ke PRIME Inventory. Data tetap perlu dilengkapi sebelum disimpan.
        </Text>
      </VStack>

      <Card bg="white" borderRadius="16px" boxShadow="sm" mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">Source API</FormLabel>
              <Select value={source} onChange={(e) => setSource(e.target.value)}>
                <option value="bss">IAS BSS (Wrapping Booth)</option>
                <option value="joumpa">IAS JOUMPA (VIP Service)</option>
                <option value="lounge">IAS Lounge</option>
              </Select>
            </FormControl>
            {source === 'joumpa' && (
              <>
                <FormControl>
                  <FormLabel fontSize="sm">Airport (Lokasi Service)</FormLabel>
                  <JoumpaAirportSelect
                    value={joumpaAirport}
                    onChange={(code) => setJoumpaAirport(code)}
                    placeholder="cgk"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Flight Type</FormLabel>
                  <Select value={joumpaFlightType} onChange={(e) => setJoumpaFlightType(e.target.value)}>
                    <option value="departure">departure</option>
                    <option value="arrival">arrival</option>
                    <option value="transfer">transfer</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Origin</FormLabel>
                  <JoumpaAirportSelect
                    value={joumpaOrigin}
                    onChange={(code) => setJoumpaOrigin(code)}
                    placeholder="cgk"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Destination</FormLabel>
                  <JoumpaAirportSelect
                    value={joumpaDestination}
                    onChange={(code) => setJoumpaDestination(code)}
                    placeholder="sin"
                  />
                </FormControl>
              </>
            )}
            {source === 'lounge' && (
              <FormControl>
                <FormLabel fontSize="sm">Branch (Airport Code)</FormLabel>
                <Input
                  placeholder="Cari airport..."
                  value={airportSearch}
                  onChange={(e) => setAirportSearch(e.target.value)}
                  autoComplete="off"
                  mb={1}
                />
                <Select
                  value={loungeBranch}
                  onChange={(e) => {
                    setLoungeBranch(e.target.value);
                    setAirportSearch('');
                  }}
                  size="sm"
                >
                  {airportOptions
                    .filter((a) => {
                      if (!airportSearch.trim()) return true;
                      const q = airportSearch.toLowerCase();
                      const code = (a.AIRPORT_CODE || '').toLowerCase();
                      const name = (a.AIRPORT_NAME || '').toLowerCase();
                      const city = (a.AIRPORT_CITY || '').toLowerCase();
                      return code.includes(q) || name.includes(q) || city.includes(q);
                    })
                    .map((a) => (
                      <option key={a.AIRPORT_CODE} value={a.AIRPORT_CODE}>
                        {a.AIRPORT_CODE} - {a.AIRPORT_NAME} ({a.AIRPORT_CITY || ''})
                      </option>
                    ))}
                </Select>
              </FormControl>
            )}
            <FormControl>
              <FormLabel fontSize="sm">Cari Produk</FormLabel>
              <Input
                placeholder="Cari nama, kode, ID, currency"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </FormControl>
          </SimpleGrid>

          <HStack mt={4} justify="space-between" align="center">
            <HStack>
              <Button
                leftIcon={loading ? <Spinner size="sm" /> : <FiRefreshCw />}
                colorScheme="blue"
                onClick={loadProducts}
                isDisabled={loading}
              >
                Refresh Source Produk
              </Button>
              <Badge colorScheme="green">{filteredProducts.length} produk</Badge>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              Last sync: {lastSyncAt ? lastSyncAt.toLocaleString() : '-'}
            </Text>
          </HStack>
        </CardBody>
      </Card>

      <Alert status="info" borderRadius="md" mb={4}>
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Produk IAS di halaman ini adalah source eksternal. Klik "Import ke PRIME" untuk prefill form, lalu review metadata,
          periode, harga, dan readiness sebelum simpan.
        </AlertDescription>
      </Alert>

      <Card bg="white" borderRadius="16px" boxShadow="sm">
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Heading size="sm" color="gray.700">
              Tabel Source Produk IAS
            </Heading>
            <TableContainer border="1px solid" borderColor="gray.100" borderRadius="md">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>No</Th>
                    <Th>Source</Th>
                    <Th>ID</Th>
                    <Th>Nama Produk</Th>
                    <Th>Kode</Th>
                    <Th isNumeric>Harga</Th>
                    <Th>Currency</Th>
                    <Th>Aksi</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredProducts.length === 0 ? (
                    <Tr>
                      <Td colSpan={8}>
                        <Text textAlign="center" color="gray.500" py={3}>
                          Belum ada data source produk IAS. Klik "Refresh Source Produk" untuk memuat data.
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    filteredProducts.map((item, idx) => (
                      <Tr key={`${item?.id || 'row'}-${idx}`}>
                        <Td>{idx + 1}</Td>
                        <Td>
                          <Badge colorScheme={item?.source === 'joumpa' ? 'purple' : item?.source === 'lounge' ? 'teal' : 'blue'}>
                            {item?.sourceLabel || item?.source || '-'}
                          </Badge>
                        </Td>
                        <Td>{item?.id ?? '-'}</Td>
                        <Td>{item?.name || '-'}</Td>
                        <Td>{item?.code || '-'}</Td>
                        <Td isNumeric>{item?.price != null ? Number(item.price).toLocaleString('id-ID') : '-'}</Td>
                        <Td>{item?.currency || '-'}</Td>
                        <Td>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            leftIcon={<FiDownload />}
                            onClick={() => handleImport(item)}
                          >
                            Import ke PRIME
                          </Button>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default IasIntegrationData;
